import React, { useCallback, useEffect, useRef, useState } from 'react';
import syncConnectQueryService from '../services/syncConnectQueryService';
import ConfirmModal from './shared/ConfirmModal';
import './OxiDequeueAdmin.css';
import './AdminSyncConnectQueries.css';

// ADMIN › PLATFORM › SYNCCONNECT › STATUS QUERIES (V613) — checks every installed SyncConnect runs once a minute
// against its property's database (/opt/utils/syncconnect-status).
//
// A status query is one read-only SELECT; the FIRST COLUMN of its FIRST ROW is checked against the expectation (no row
// is an empty value). Every active query goes to a client in one command, so one that errors fails the whole batch:
// Test it on a project first — the answer arrives on the Queries tab — then make it active. When a check fails, the
// box it names on the project's card (Source or Client) turns red (Error) or amber (Warning) with its message.

const EMPTY = {
    id: null, title: '', description: '', sql_text: '', check_op: 'NOT_NULL', expected_value: '',
    highlight: 'SOURCE', severity: 'ERROR', fail_message: '', active: 'N', sort_order: 0, templates: [],
};
const TEST_POLL_MS = 3000;
const TEST_GIVE_UP_MS = 3 * 60 * 1000;   // a client that has not answered in this long is not going to soon
const errText = (e) => e.response?.data?.error?.message || e.response?.data?.message || e.message;
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

function Outcome({ outcome, stale }) {
    if (!outcome) return <span className="scq-status">not run</span>;
    const cls = stale ? 'queued' : outcome === 'PASS' ? 'returned' : 'error';
    return <span className={`scq-status scq-status-${cls}`}>{stale ? `${outcome} (stale)` : outcome}</span>;
}

export default function AdminSyncConnectStatusQueries() {
    const [rows, setRows] = useState([]);
    const [ops, setOps] = useState([]);
    const [templates, setTemplates] = useState([]);   // the sync templates a query can run on (V615)
    const [results, setResults] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [editing, setEditing] = useState(null);       // the form: a row or EMPTY, plus { saving, error, testProject }
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [resultsFor, setResultsFor] = useState('');    // '' all queries, else a status query id
    // THE TEST, answered in the dialog: { adhocId, startedAt, status, value, outcome, error, rows } — polled until the
    // client answers, judged by the server against the editor's current expectation.
    const [testRun, setTestRun] = useState(null);
    const testTimer = useRef(null);
    useEffect(() => () => clearTimeout(testTimer.current), []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [q, r] = await Promise.all([
                syncConnectQueryService.statusQueries(),
                syncConnectQueryService.statusResults(resultsFor ? { statusQueryId: resultsFor } : {}),
            ]);
            setRows((q.data || q).rows || []);
            setOps((q.data || q).ops || []);
            setTemplates((q.data || q).templates || []);
            setResults((r.data || r).rows || []);
            setError(null);
        } catch (e) {
            setError(errText(e));
        } finally {
            setLoading(false);
        }
    }, [resultsFor]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        syncConnectQueryService.projects().then(r => setProjects((r.data || r).rows || [])).catch(() => {});
    }, []);

    const opOf = (op) => ops.find(o => o.op === op) || { label: op, needsExpected: false };
    const set = (k, v) => setEditing(f => ({ ...f, [k]: v }));

    const save = async () => {
        setEditing(f => ({ ...f, saving: true, error: null }));
        try {
            const body = { ...editing };
            delete body.saving; delete body.error; delete body.testProject;
            if (editing.id) await syncConnectQueryService.updateStatusQuery(editing.id, body);
            else await syncConnectQueryService.createStatusQuery(body);
            setEditing(null);
            await load();
        } catch (e) {
            setEditing(f => ({ ...f, saving: false, error: errText(e) }));
        }
    };

    const test = async () => {
        if (!editing.testProject) { setEditing(f => ({ ...f, error: 'Choose a project to test on' })); return; }
        setEditing(f => ({ ...f, saving: true, error: null }));
        try {
            const res = await syncConnectQueryService.testStatusQuery(editing.id,
                { syncProjectId: editing.testProject, sql_text: editing.sql_text, title: editing.title });
            const q = res.data || res;
            setEditing(f => ({ ...f, saving: false }));
            pollTest(q.id, Date.now(), { check_op: editing.check_op, expected_value: editing.expected_value });
        } catch (e) {
            setEditing(f => ({ ...f, saving: false, error: errText(e) }));
        }
    };

    const pollTest = (adhocId, startedAt, expectation) => {
        clearTimeout(testTimer.current);
        setTestRun({ adhocId, startedAt, status: 'QUEUED' });
        const tick = async () => {
            try {
                const res = await syncConnectQueryService.statusTestResult(editing.id, { adhocId, ...expectation });
                const r = res.data || res;
                setTestRun({ adhocId, startedAt, ...r });
                if (r.done) return;
            } catch (e) {
                setTestRun(t => ({ ...(t || {}), adhocId, startedAt, error: errText(e) }));
            }
            if (Date.now() - startedAt > TEST_GIVE_UP_MS) {
                setTestRun(t => ({ ...(t || {}), timedOut: true }));
                return;
            }
            testTimer.current = setTimeout(tick, TEST_POLL_MS);
        };
        testTimer.current = setTimeout(tick, TEST_POLL_MS);
    };
    const closeEditor = () => { clearTimeout(testTimer.current); setTestRun(null); setEditing(null); };

    const toggle = async (row) => {
        try {
            await syncConnectQueryService.updateStatusQuery(row.id, { active: row.active === 'Y' ? 'N' : 'Y' });
            await load();
        } catch (e) { setError(errText(e)); }
    };

    const remove = async (row) => {
        setConfirmDelete(null);
        try {
            await syncConnectQueryService.removeStatusQuery(row.id);
            await load();
        } catch (e) { setError(errText(e)); }
    };

    const templateName = (id) => (templates.find(t => t.id === id) || {}).name || `template ${id}`;
    const toggleTemplate = (id) => setEditing(f => ({
        ...f, templates: (f.templates || []).includes(id) ? f.templates.filter(t => t !== id) : [...(f.templates || []), id],
    }));

    const describe = (r) => `${opOf(r.check_op).label}${r.expected_value != null && r.expected_value !== '' ? ` ${r.expected_value}` : ''}`;

    return (
        <div>
            <div className="acp-header">
                <div>
                    <p className="acp-subtitle">
                        Checks every SyncConnect runs once a minute against its property&apos;s database, all in one message.
                        Each query runs only on projects of the sync templates it is assigned to, and only where SyncConnect
                        is used. Each query is one SELECT; the first column of its first row is checked. When a check fails, the box it
                        names on the project&apos;s card turns red (Error) or amber (Warning). One query that errors fails the
                        whole batch — test it on a project before making it active.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button className="acp-btn acp-btn-secondary" onClick={load} disabled={loading}>Refresh</button>
                    <button className="acp-btn acp-btn-primary" onClick={() => setEditing({ ...EMPTY })}>+ New status query</button>
                </div>
            </div>

            {error && <div className="os-msg os-msg-error" onClick={() => setError(null)}>{error}</div>}
            {notice && <div className="scq-wait" onClick={() => setNotice(null)}>{notice}</div>}

            <div className="acp-card">
                <div className="acp-card-header">
                    <span className="acp-card-title">{rows.length} status quer{rows.length === 1 ? 'y' : 'ies'}</span>
                </div>
                {loading ? <div className="acp-empty">Loading…</div>
                    : rows.length === 0 ? <div className="acp-empty">No status queries yet.</div>
                    : (
                    <table className="acp-table">
                        <thead>
                            <tr>
                                <th>Active</th><th>Title</th><th>Check</th><th>Runs on</th><th>Shows on</th>
                                <th>Projects passing / failing</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id}>
                                    <td><input type="checkbox" checked={r.active === 'Y'} onChange={() => toggle(r)} title="Send to every SyncConnect" /></td>
                                    <td><strong>{r.title}</strong>{r.description && <div className="scq-meta">{r.description}</div>}</td>
                                    <td className="scq-mono">{describe(r)}</td>
                                    <td>{(r.templates || []).length
                                        ? r.templates.map(templateName).join(', ')
                                        : <span style={{ color: '#b45309' }}>no templates — runs nowhere</span>}</td>
                                    <td>{r.highlight === 'CLIENT' ? 'Client' : 'Source'} · <span className={`scq-status scq-status-${r.severity === 'ERROR' ? 'error' : 'delivered'}`}>{r.severity}</span></td>
                                    <td>{r.passing || 0} / <span style={{ color: r.failing ? '#b91c1c' : undefined, fontWeight: r.failing ? 600 : 400 }}>{r.failing || 0}</span></td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setEditing({ ...r, expected_value: r.expected_value || '', fail_message: r.fail_message || '', description: r.description || '', templates: r.templates || [] })}>Edit</button>{' '}
                                        <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={() => setResultsFor(String(r.id))}>Results</button>{' '}
                                        <button className="acp-btn acp-btn-danger acp-btn-sm" onClick={() => setConfirmDelete(r)}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="acp-card" style={{ marginTop: '1rem' }}>
                <div className="acp-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
                    <span className="acp-card-title">Latest results</span>
                    <select className="odq-filter-select" value={resultsFor} onChange={e => setResultsFor(e.target.value)}>
                        <option value="">All status queries</option>
                        {rows.map(r => <option key={r.id} value={r.id}>{r.title}</option>)}
                    </select>
                </div>
                {results.length === 0 ? <div className="acp-empty">No results yet — they arrive a minute after a query is made active.</div> : (
                    <table className="acp-table">
                        <thead>
                            <tr><th>Project</th><th>Status query</th><th>Outcome</th><th>Value</th><th>Checked</th></tr>
                        </thead>
                        <tbody>
                            {results.map(r => (
                                <tr key={`${r.sync_project_id}-${r.status_query_id}`}>
                                    <td>{r.sync_project_id} · {r.projectname || r.hotelname}</td>
                                    <td>{r.title}</td>
                                    <td><Outcome outcome={r.outcome} stale={!!Number(r.stale)} /></td>
                                    <td className="scq-mono" title={r.error_message || ''}>{r.outcome === 'ERROR' ? (r.error_message || '').slice(0, 120) : (r.value ?? '(empty)')}</td>
                                    <td>{when(r.checked_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="odq-modal-overlay" onClick={() => !editing.saving && closeEditor()}>
                    <div className="odq-modal scq-compose" onClick={e => e.stopPropagation()}>
                        <div className="odq-modal-head">
                            <span>{editing.id ? `Status query ${editing.id}` : 'New status query'}</span>
                            <button className="acp-btn acp-btn-secondary acp-btn-sm" onClick={closeEditor} disabled={editing.saving}>Close</button>
                        </div>
                        <div className="odq-modal-body scq-form">
                            <label>Title
                                <input value={editing.title} placeholder="e.g. OXI business interface points at SyncConnect"
                                       onChange={e => set('title', e.target.value)} />
                            </label>
                            <label>Description (optional)
                                <input value={editing.description} onChange={e => set('description', e.target.value)} />
                            </label>
                            <label>SQL (one SELECT — the first column of the first row is the value checked; no row is empty)
                                <textarea className="scq-sql" rows={10} value={editing.sql_text} spellCheck={false}
                                          style={{ minHeight: '22vh' }}
                                          placeholder="SELECT url FROM … WHERE …"
                                          onChange={e => set('sql_text', e.target.value)} />
                            </label>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <label style={{ flex: '1 1 220px' }}>The value
                                    <select value={editing.check_op} onChange={e => set('check_op', e.target.value)}>
                                        {ops.map(o => <option key={o.op} value={o.op}>{o.label}</option>)}
                                    </select>
                                </label>
                                {opOf(editing.check_op).needsExpected && (
                                    <label style={{ flex: '2 1 320px' }}>Expected
                                        <input className="scq-mono" value={editing.expected_value}
                                               placeholder={editing.check_op.includes('MATCHES') ? 'http://localhost:8081/*' : ''}
                                               onChange={e => set('expected_value', e.target.value)} />
                                    </label>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <label style={{ flex: '1 1 160px' }}>When it fails, show on
                                    <select value={editing.highlight} onChange={e => set('highlight', e.target.value)}>
                                        <option value="SOURCE">Source</option>
                                        <option value="CLIENT">Client (SyncConnect)</option>
                                    </select>
                                </label>
                                <label style={{ flex: '1 1 140px' }}>As
                                    <select value={editing.severity} onChange={e => set('severity', e.target.value)}>
                                        <option value="ERROR">Error (red)</option>
                                        <option value="WARNING">Warning (amber)</option>
                                    </select>
                                </label>
                                <label style={{ flex: '0 1 100px' }}>Order
                                    <input type="number" value={editing.sort_order} onChange={e => set('sort_order', e.target.value)} />
                                </label>
                                <label style={{ flex: '0 1 100px' }}>Active
                                    <select value={editing.active} onChange={e => set('active', e.target.value)}>
                                        <option value="N">No</option>
                                        <option value="Y">Yes</option>
                                    </select>
                                </label>
                            </div>
                            <div className="scq-templates">
                                <span className="scq-templates-label">Runs on projects of these sync templates (that use SyncConnect)</span>
                                {templates.map(t => (
                                    <label key={t.id} className="scq-template">
                                        <input type="checkbox" checked={(editing.templates || []).includes(t.id)} onChange={() => toggleTemplate(t.id)} />
                                        <span>{t.name}</span>
                                        <span className="scq-meta" style={{ margin: 0 }}>
                                            {t.offline === 'Y' ? 'offline edition — never polls, never runs' : `${t.syncconnect_projects} SyncConnect project${t.syncconnect_projects === 1 ? '' : 's'}`}
                                        </span>
                                    </label>
                                ))}
                            </div>
                            <label>Message on the card when it fails
                                <input value={editing.fail_message} placeholder="e.g. The OXI business interface does not point at SyncConnect (http://localhost:8081)"
                                       onChange={e => set('fail_message', e.target.value)} />
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                                <label style={{ flex: '1 1 320px' }}>Test on project (an ordinary query — answer on the Queries tab)
                                    <select value={editing.testProject || ''} onChange={e => set('testProject', e.target.value)}>
                                        <option value="">Choose a project…</option>
                                        {projects.map(p => (
                                            <option key={p.syncid} value={p.syncid}>
                                                {p.syncid} · {p.projectname || p.hotelname}{p.dequeue_last_contact ? ` — last polled ${when(p.dequeue_last_contact)}` : ' — never polled'}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                <button className="acp-btn acp-btn-secondary" onClick={test} disabled={editing.saving || !editing.sql_text}>Test</button>
                            </div>
                            {testRun && (
                                <div className={`scq-test scq-test-${(testRun.outcome || (testRun.timedOut ? 'timeout' : 'waiting')).toLowerCase()}`}>
                                    {!testRun.done && !testRun.timedOut && <>Waiting for SyncConnect to answer test query {testRun.adhocId} ({testRun.status === 'DELIVERED' ? 'collected, running' : 'queued'})…</>}
                                    {testRun.timedOut && !testRun.done && <>No answer to test query {testRun.adhocId} after 3 minutes — is the client polling? It may still arrive on the Queries tab.</>}
                                    {testRun.done && testRun.outcome === 'ERROR' && <><strong>ERROR</strong> — the query did not run: {testRun.error}</>}
                                    {testRun.done && testRun.outcome !== 'ERROR' && <>
                                        <strong>{testRun.outcome}</strong> — value <code>{testRun.value ?? '(empty)'}</code>
                                        {testRun.rows === 0 && ' (no row came back)'} · {opOf(editing.check_op).label}{opOf(editing.check_op).needsExpected ? ` ${editing.expected_value}` : ''}
                                    </>}
                                </div>
                            )}
                            {editing.error && <div className="os-msg os-msg-error">{editing.error}</div>}
                            <div className="scq-actions">
                                <button className="acp-btn acp-btn-secondary" onClick={closeEditor} disabled={editing.saving}>Cancel</button>
                                <button className="acp-btn acp-btn-primary" onClick={save} disabled={editing.saving || !editing.title || !editing.sql_text}>
                                    {editing.saving ? 'Saving…' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {confirmDelete && (
                <ConfirmModal
                    title={`Delete status query "${confirmDelete.title}"?`}
                    message="It stops being sent to SyncConnect and its results are cleared from every project's card."
                    confirmLabel="Delete"
                    danger
                    onConfirm={() => remove(confirmDelete)}
                    onCancel={() => setConfirmDelete(null)}
                />
            )}
        </div>
    );
}
